import { useState, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { UploadCloud, FileText, ImageIcon, X, LinkIcon } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/Admin";

export default function AdminMediaUploadPage() {
  const [files, setFiles] = useState([]);
  const { uploadFiles, uploadFileFromUrl } = useAdminAuth();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [isLinkUploading, setIsLinkUploading] = useState(false);
  const [linkUploadStatus, setLinkUploadStatus] = useState("");

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(
      acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      )
    );
    setUploadProgress(0);
    setUploadStatus("");
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  const handleRemoveFile = (fileName) => {
    setFiles(files.filter((file) => file.name !== fileName));
    setUploadProgress(0);
    setUploadStatus("");
  };

  const handleFileUpload = async () => {
    if (files.length === 0) {
      setUploadStatus("Please select files to upload.");
      toast({
        title: "No files selected",
        description: "Please select files to upload.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadStatus("Uploading...");

    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));

      await uploadFiles(formData);

      setUploadStatus("Upload complete!");
      setFiles([]); // Clear after upload
    } catch {
      setUploadStatus("Upload failed.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleLinkUpload = async () => {
    if (!linkUrl.trim()) {
      setLinkUploadStatus("Please enter a valid URL.");
      toast({
        title: "URL Required",
        description: "Please enter a valid URL to upload from link.",
        variant: "destructive",
      });
      return;
    }

    setIsLinkUploading(true);
    setLinkUploadStatus("Uploading from link...");

    try {
      await uploadFileFromUrl(linkUrl);
      setLinkUploadStatus("Upload from link complete!");
      setLinkUrl("");
    } catch {
      setLinkUploadStatus("Failed to upload from link.");
    } finally {
      setIsLinkUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Upload Media</h1>

      <Tabs defaultValue="file-upload" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="file-upload">
            <UploadCloud className="mr-2 h-4 w-4" /> Upload Files
          </TabsTrigger>
          <TabsTrigger value="link-upload">
            <LinkIcon className="mr-2 h-4 w-4" /> Upload from Link
          </TabsTrigger>
        </TabsList>

        <TabsContent value="file-upload">
          <Card>
            <CardHeader>
              <CardTitle>Upload New Files</CardTitle>
              <CardDescription>
                Drag and drop your files here, or click to browse.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div
                {...getRootProps()}
                className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-8 text-center transition-colors hover:border-gray-400 dark:border-gray-700 dark:hover:border-gray-600 cursor-pointer"
              >
                <input {...getInputProps()} />
                {isDragActive ? (
                  <p className="text-gray-500 dark:text-gray-400">
                    Drop the files here ...
                  </p>
                ) : (
                  <>
                    <UploadCloud className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                    <p className="mt-2 text-gray-500 dark:text-gray-400">
                      Drag 'n' drop some files here, or click to select files
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-4 bg-transparent"
                    >
                      Browse Files
                    </Button>
                  </>
                )}
              </div>

              {files.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-lg font-semibold">Selected Files:</h3>
                  <ul className="mt-2 space-y-2">
                    {files.map((file) => (
                      <li
                        key={file.name}
                        className="flex items-center justify-between rounded-md p-3"
                      >
                        <div className="flex items-center gap-3">
                          {file.type.startsWith("image/") ? (
                            <ImageIcon className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <FileText className="h-5 w-5 text-muted-foreground" />
                          )}
                          <span>{file.name}</span>
                          <span className="text-sm text-muted-foreground">
                            ({(file.size / 1024).toFixed(2)} KB)
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveFile(file.name)}
                        >
                          <X className="h-4 w-4" />
                          <span className="sr-only">Remove file</span>
                        </Button>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={handleFileUpload}
                    disabled={isUploading}
                    className="mt-4 w-full"
                  >
                    {isUploading ? "Uploading..." : "Upload All Files"}
                  </Button>
                  {isUploading && (
                    <div className="mt-4">
                      <Label htmlFor="upload-progress">Upload Progress</Label>
                      <Progress
                        value={uploadProgress}
                        id="upload-progress"
                        className="w-full"
                      />
                    </div>
                  )}
                  {uploadStatus && (
                    <p className="mt-2 text-center text-sm text-muted-foreground">
                      {uploadStatus}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="link-upload">
          <Card>
            <CardHeader>
              <CardTitle>Upload from URL</CardTitle>
              <CardDescription>
                Enter the URL of the file you want to upload.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div>
                <Label htmlFor="file-url">File URL</Label>
                <Input
                  id="file-url"
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  disabled={isLinkUploading}
                />
              </div>
              <Button
                onClick={handleLinkUpload}
                disabled={isLinkUploading}
                className="w-full"
              >
                {isLinkUploading ? "Uploading..." : "Upload from Link"}
              </Button>
              {linkUploadStatus && (
                <p className="mt-2 text-center text-sm text-muted-foreground">
                  {linkUploadStatus}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
